import { Button, DialogContentText, IconButton } from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import ActionDialog from "@saleor/components/ActionDialog";
import AssignProductDialog from "@saleor/components/AssignProductDialog";
import { DEFAULT_INITIAL_SEARCH_DATA } from "@saleor/config";
import useBulkActions from "@saleor/hooks/useBulkActions";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import { createPaginationState } from "@saleor/hooks/usePaginator";
import InstagramDetailsPage, {
  InstagramDetailsPageFormData
} from "@saleor/instagram/components/InstagramDetailsPage";
import InstagramTagAddDialog from "@saleor/instagram/components/InstagramTagAddDialog";
import InstagramTagUpdateDialog from "@saleor/instagram/components/InstagramTagUpdateDialog";
import {
  useInstagCreateMutation,
  useInsTagDeleteMutation,
  useInstagramBulkDeleteTagMutation,
  useInstagramCreateProductinsMutation,
  useInstagramUpdate,
  useInsTagUpdateMutation,
  useProductVisibleProductInsMutation,
  useUnassignInstagramProductMutation
} from "@saleor/instagram/mutations";
import {
  useInsProductVariantQuery,
  useInstagramDetailsQuery
} from "@saleor/instagram/queries";
import {
  instagramListUrl,
  InstagramPathUrlQueryParams,
  instagramUrl,
  InstagramUrlDialog
} from "@saleor/instagram/urls";
import { commonMessages } from "@saleor/intl";
import { maybe } from "@saleor/misc";
import { productUrl } from "@saleor/products/urls";
import useProductSearch from "@saleor/searches/useProductSearch";
import useProductVariantSearch from "@saleor/searches/useProductVariantSearch";
import createDialogActionHandlers from "@saleor/utils/handlers/dialogActionHandlers";
import { mapEdgesToItems } from "@saleor/utils/maps";
import React, { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

interface InstagramDetailsProps {
  id: string;
  params: InstagramPathUrlQueryParams;
}

export const InstagramDetails: React.FC<InstagramDetailsProps> = ({
  id,
  params
}) => {
  const navigate = useNavigator();

  const notify = useNotifier();

  const { data, loading } = useInstagramDetailsQuery({
    variables: { id }
  });

  const intl = useIntl();

  const [productVariant, setProductVariant] = useState<number>(-1);

  const [updateInstagram, updateInstagramOpts] = useInstagramUpdate({
    onCompleted: data => {
      if (data.inseditUpdate.errors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage(commonMessages.savedChanges)
        });
      }
    }
  });

  const handleSubmit = async (data: InstagramDetailsPageFormData) => {
    const result = await updateInstagram({
      variables: {
        id,
        input: {
          published: data.published,
          // image: data.image
          startTime: Number(data.startTime),
          intervalTime: Number(data.intervalTime)
        }
      }
    });
    return result.data.inseditUpdate.errors;
  };

  const [openModal, closeModal] = createDialogActionHandlers<
    InstagramUrlDialog,
    InstagramPathUrlQueryParams
  >(navigate, params => instagramUrl(id, params), params);

  const { isSelected, listElements, reset, toggle, toggleAll } = useBulkActions(
    (() => {
      if (typeof params?.ids !== "string") {
        return params.ids;
      } else {
        return ([params.ids] as unknown) as string[];
      }
    })()
  );
  const [
    unassignProduct,
    unassignProductOpts
  ] = useUnassignInstagramProductMutation({
    onCompleted: data => {
      if (data.instagramRemoveProducts.errors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Deleted product from Instagarm"
          })
        });
        reset();
        closeModal();
      }
    }
  });

  const [
    instagramBulkDeleteTag,
    instagramBulkDeleteTagOpts
  ] = useInstagramBulkDeleteTagMutation({
    onCompleted: data => {
      if (data.instagramBulkDeleteTag.errors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Deleted tag from Instagarm"
          })
        });
        navigate(instagramUrl(id), true);
      }
    }
  });

  const [
    assignProductIns,
    assignProductInsOpts
  ] = useInstagramCreateProductinsMutation({
    onCompleted: data => {
      if (data.instagramAddProductCreate.errors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Added Product to Ins"
          })
        });
        navigate(instagramUrl(id), true);
      }
    }
  });

  const paginationState = createPaginationState(8, params);

  const { search, loadMore, result } = useProductSearch({
    variables: {
      after: null,
      first: 8,
      query: ""
    }
  });

  const [addInsTag] = useInstagCreateMutation({
    onCompleted: data => {
      if (data.instagCreate.errors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Added Product to Ins"
          })
        });
        navigate(instagramUrl(id), true);
      }
    }
  });

  const {
    loadMore: loadMoreVariants,
    search: searchVariants,
    result: resultVariants
  } = useProductVariantSearch({
    variables: DEFAULT_INITIAL_SEARCH_DATA
  });

  const variants = maybe(() => resultVariants.data.search.edges, []).map(
    edge => edge.node
  );

  const [instagramTagDelete] = useInsTagDeleteMutation({
    onCompleted: data => {
      if (data.instagDelete.errors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "tag deleted",
            description: "instagram tag deleted"
          })
        });
        navigate(instagramUrl(id));
      }
    }
  });

  const [instagramTagUpdate] = useInsTagUpdateMutation({
    onCompleted: data => {
      if (data.instagUpdate.errors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage(commonMessages.savedChanges)
        });
        navigate(instagramUrl(id));
      }
    }
  });

  const insProductVariant = variantId => {
    const result = useInsProductVariantQuery({
      variables: {
        id: variantId
      }
    });
    return result?.data?.productVariant;
  };

  const [visibleProductIns] = useProductVisibleProductInsMutation({
    onCompleted: data => {
      if (data.productVisibleProductIns.errors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Visible ProductIns for Instagram"
          })
        });
        navigate(instagramUrl(id));
      }
    }
  });
  return (
    <>
      <InstagramDetailsPage
        onAdd={() => openModal("assign")}
        addInsTag={() => openModal("add-tag")}
        deleteInsTag={id =>
          instagramTagDelete({
            variables: {
              id
            }
          })
        }
        disabled={loading}
        instagram={data?.instagram}
        onSubmit={handleSubmit}
        onBack={() => navigate(instagramListUrl())}
        saveButtonBarState={updateInstagramOpts.status}
        onNextPage={null}
        onPreviousPage={null}
        reverseVisible={productIns =>
          visibleProductIns({
            variables: {
              ids: productIns.map(productIns => productIns.id)
            }
          })
        }
        toolbar={
          <Button
            color="primary"
            onClick={() =>
              openModal("unassign", {
                ids: listElements
              })
            }
          >
            <FormattedMessage
              defaultMessage="Unassign"
              description="unassign product from collection, button"
            />
          </Button>
        }
        toolbarTag={
          <IconButton
            color="primary"
            onClick={() =>
              openModal("unassignTag", {
                ids: listElements
              })
            }
          >
            <DeleteIcon color="primary" />
          </IconButton>
        }
        toggleAll={toggleAll}
        isChecked={isSelected}
        selected={listElements.length}
        toggle={toggle}
        onRowClick={id => () => navigate(productUrl(id))}
        onRowTagClick={id => () => {
          setProductVariant(
            data?.instagram?.instagramtag?.find(tag => id === tag.id)
              ?.productvariant?.variantId
          );
          openModal("updateTag", { id });
        }}
        onProductUnassign={(productId, event) => {
          event.stopPropagation();
          openModal("unassign", {
            ids: [productId]
          });
        }}
        fetchMoreVariants={{
          hasMore: resultVariants.data?.search.pageInfo.hasNextPage,
          loading: resultVariants.loading,
          onFetchMore: loadMoreVariants
        }}
        fetchVariants={searchVariants}
        variants={variants}
      />
      <AssignProductDialog
        confirmButtonState={assignProductInsOpts.status}
        hasMore={result.data?.search?.pageInfo.hasNextPage}
        open={params.action === "assign"}
        onFetch={search}
        onFetchMore={loadMore}
        loading={result.loading}
        onClose={closeModal}
        onSubmit={products =>
          assignProductIns({
            variables: {
              ...paginationState,
              instagramId: id,
              products: products.map(product => product.id)
            }
          })
        }
        products={mapEdgesToItems(result?.data?.search)?.filter(
          suggestedProduct => suggestedProduct.id
        )}
      />
      <ActionDialog
        confirmButtonState={unassignProductOpts.status}
        onClose={closeModal}
        onConfirm={() =>
          unassignProduct({
            variables: {
              instagramId: id,
              productIds: params.ids
            }
          })
        }
        open={params.action === "unassign"}
        title={intl.formatMessage({
          defaultMessage: "Unassign products from Instagram",
          description: "dialog title"
        })}
      >
        <DialogContentText>
          <FormattedMessage
            defaultMessage="{counter,plural,one{Are you sure you want to unassign this product?} other{Are you sure you want to unassign {displayQuantity} products?}}"
            values={{
              counter: maybe(() => {
                if (typeof params?.ids === "string") {
                  return 1;
                } else {
                  return params?.ids?.length;
                }
              }),
              displayQuantity: (
                <strong>{maybe(() => params?.ids?.length)}</strong>
              )
            }}
          />
        </DialogContentText>
      </ActionDialog>
      <InstagramTagAddDialog
        open={params.action === "add-tag"}
        onClose={closeModal}
        onSubmit={sku =>
          addInsTag({
            variables: {
              instagramId: id,
              input: {
                pcx: 50,
                pcy: 50,
                mbx: 50,
                mby: 50,
                productvariant: sku
              }
            }
          })
        }
      />
      <ActionDialog
        confirmButtonState={instagramBulkDeleteTagOpts.status}
        onClose={closeModal}
        onConfirm={() =>
          instagramBulkDeleteTag({
            variables: {
              ids: params.ids
            }
          })
        }
        open={params.action === "unassignTag"}
        title={intl.formatMessage({
          defaultMessage: "Unassign tag from instagram",
          description: "dialog title"
        })}
      >
        <DialogContentText>
          <FormattedMessage
            defaultMessage="{counter,plural,one{Are you sure you want to delete this tag?} other{Are you sure you want to delete {displayQuantity} tags?}}"
            values={{
              counter: maybe(() => {
                if (typeof params?.ids === "string") {
                  return 1;
                } else {
                  return params?.ids?.length;
                }
              }),
              displayQuantity: (
                <strong>{maybe(() => params?.ids?.length)}</strong>
              )
            }}
          />
        </DialogContentText>
      </ActionDialog>
      <InstagramTagUpdateDialog
        open={params.action === "updateTag"}
        onClose={closeModal}
        instagramTag={data?.instagram?.instagramtag.find(
          tag => params.id === tag.id
        )}
        instagramImage={data?.instagram?.image}
        productVariant={productVariant}
        setProductVariant={setProductVariant}
        tagProductVariant={insProductVariant(productVariant)}
        onSubmit={input =>
          instagramTagUpdate({
            variables: {
              id,
              input: {
                ...input,
                productvariant: input.productvariant
              }
            }
          })
        }
      />
    </>
  );
};

export default InstagramDetails;
